import type { LucideIcon } from 'lucide-react'

import {
    ChartColumn,
    ChartLine,
    FileText,
    Receipt,
    Sparkles,
    Target
} from 'lucide-react'

export type FeatureKey =
    | 'automated-reporting'
    | 'expense-tracking'
    | 'ai-insights'
    | 'cash-flow-forecasting'
    | 'revenue-analytics'
    | 'budget-management'

export type Feature = {
    id: FeatureKey
    number: string
    title: string
    headline: string
    summary: string
    details: string
    points: Array<string>
    Icon: LucideIcon
}

export const FEATURES: Array<Feature> = [
    {
        id: 'automated-reporting',
        number: '01',
        title: 'Automated Financial Reporting',
        headline: 'Reports that write themselves.',
        summary: 'Profit and loss, balance sheet and cash flow statements are generated from reconciled data the moment your books close, then delivered to the people who need them.',
        details: 'Choose a schedule once and Veyra takes it from there. Every report is built from the same reconciled ledger, so the numbers in the board pack always match the numbers in your dashboard. AI drafts the commentary for each material variance, and your team edits instead of writing from scratch.',
        points: [
            'Scheduled delivery to email and Slack',
            'Variance commentary drafted by AI',
            'One-click export to PDF, Excel and Google Sheets'
        ],
        Icon: FileText
    },
    {
        id: 'expense-tracking',
        number: '02',
        title: 'Real-Time Expense Tracking',
        headline: 'Every transaction, categorized as it happens.',
        summary: 'Card and bank activity flows in throughout the day, is categorized automatically and matched to receipts, so you always know exactly where money is going.',
        details: 'Veyra connects to your banks and card programs and pulls new activity throughout the day. Each transaction is categorized with the rules your team already uses, receipts are matched from email and uploads, and anything unusual is flagged for review before it becomes a month-end surprise.',
        points: [
            'Automatic receipt and invoice matching',
            'Categorization that learns your rules',
            'Instant alerts for duplicate or unusual spend'
        ],
        Icon: Receipt
    },
    {
        id: 'ai-insights',
        number: '03',
        title: 'AI-Powered Insights',
        headline: 'Answers before you ask.',
        summary: 'Veyra watches every account and explains what changed, why it changed and what to do next, in plain language your whole team can act on.',
        details: 'Instead of digging through reports, ask a question the way you would ask your controller. Veyra answers with the numbers, the drivers behind them and a suggested next step. Every answer links back to the underlying transactions, so you can always check the work.',
        points: [
            'Anomaly and duplicate detection',
            'Plain-language explanations of every variance',
            'Ask questions about your numbers in chat'
        ],
        Icon: Sparkles
    },
    {
        id: 'cash-flow-forecasting',
        number: '04',
        title: 'Cash Flow Forecasting',
        headline: 'See your cash position months ahead.',
        summary: 'A rolling forecast built from real payment behavior, recurring bills and payroll, with scenarios that show how each decision changes your outlook.',
        details: 'Veyra learns how each customer actually pays, layers in recurring bills, payroll and planned hires, and refreshes the forecast every day. Compare scenarios side by side and see exactly how a hiring plan or a slower quarter changes your cash position.',
        points: [
            '13-week and 12-month horizons',
            'Base, conservative and growth scenarios',
            'Forecast versus actual tracking'
        ],
        Icon: ChartLine
    },
    {
        id: 'revenue-analytics',
        number: '05',
        title: 'Revenue Analytics',
        headline: 'Understand what drives growth.',
        summary: 'Break revenue down by product, customer and cohort. Track recurring revenue, retention and expansion without building a single spreadsheet.',
        details: 'Connect billing, your CRM and your ledger, and Veyra reconciles them into one revenue picture. Recurring revenue, retention and expansion are calculated automatically, and every metric can be broken down by segment, plan or cohort in a click.',
        points: [
            'MRR, ARR and net revenue retention',
            'Cohort and segment breakdowns',
            'Reconciled with billing, CRM and ledger'
        ],
        Icon: ChartColumn
    },
    {
        id: 'budget-management',
        number: '06',
        title: 'Smart Budget Management',
        headline: 'Budgets people actually follow.',
        summary: 'Give every team a live budget with an owner, real-time progress and early warnings when spend is trending over, long before month end.',
        details: 'Set budgets by department, project or vendor and assign an owner to each. Veyra tracks spend against plan in real time, projects where every budget will land and alerts owners in Slack or email when the trajectory changes, so finance never has to chase updates.',
        points: [
            'Department, project and vendor budgets',
            'Trajectory-based alerts in Slack and email',
            'Approval rules by amount and category'
        ],
        Icon: Target
    }
]
